import React, { ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'error-boundary';
const IS_PROD = import.meta.env.PROD;

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
    copied: false,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, copied: false };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Logger.exception(error, 'React ErrorBoundary', LOG_TAG);

    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
      tags: {
        boundary: 'app-error-boundary',
      },
    });

    if (errorInfo.componentStack) {
      Logger.error('Component stack captured', LOG_TAG, {
        componentStack: errorInfo.componentStack,
      });
    }

    this.setState({ errorInfo });
  }

  private buildDiagnostics(): string {
    const { error, errorInfo } = this.state;
    const userAgent =
      typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
    const lines = [
      'Chill Airwaves - Error Boundary',
      `Timestamp: ${new Date().toISOString()}`,
      `User Agent: ${userAgent}`,
    ];

    if (error) {
      lines.push(`Error: ${error.name}: ${error.message}`);
      if (error.stack) {
        lines.push(`Stack:\n${error.stack}`);
      }
    }

    if (errorInfo?.componentStack) {
      lines.push(`Component Stack:\n${errorInfo.componentStack}`);
    }

    return lines.join('\n');
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleCopyDiagnostics = async () => {
    const diagnostics = this.buildDiagnostics();

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(diagnostics);
        this.setState({ copied: true });
        return;
      }

      if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = diagnostics;
        textarea.setAttribute('readonly', 'true');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.setState({ copied: true });
      }
    } catch (copyError) {
      Logger.warn('Failed to copy diagnostics', LOG_TAG, { error: copyError });
      this.setState({ copied: false });
    }
  };

  override render() {
    const { hasError, error, errorInfo, copied } = this.state;

    if (!hasError) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-12"
      >
        <div className="w-full max-w-xl space-y-4 text-center">
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="text-sm text-white/70">
            The app hit an unexpected error. You can reload or copy diagnostics for
            support.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md bg-white text-black hover:bg-white/90 transition"
              onClick={this.handleReload}
            >
              Reload
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-md border border-white/30 hover:border-white/60 transition"
              onClick={this.handleCopyDiagnostics}
            >
              Copy diagnostics
            </button>
          </div>
          {copied && (
            <div className="text-xs text-emerald-400">
              Diagnostics copied to clipboard.
            </div>
          )}

          {!IS_PROD && (error || errorInfo?.componentStack) && (
            <details className="text-left bg-white/5 border border-white/10 rounded-lg p-4">
              <summary className="cursor-pointer text-sm">Error details</summary>
              {error?.stack && (
                <pre className="mt-2 text-xs whitespace-pre-wrap">{error.stack}</pre>
              )}
              {errorInfo?.componentStack && (
                <pre className="mt-2 text-xs whitespace-pre-wrap">
                  {errorInfo.componentStack}
                </pre>
              )}
            </details>
          )}
        </div>
      </div>
    );
  }
}
